import React, {useState, useEffect, useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import {connect, batch, useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import _ from 'lodash';

import {resetVirtualTrayShownModal, setVirtualTrayShownModal} from 'stores/actions/virtualTrayAction';
import {
  getDeckLabwareRefs,
  getIsAspirateDispenseModalShown,
  getVirtualTrayShownModalType,
} from 'stores/selectors/virtualTraySelectors';
import {getCards} from 'stores/selectors/cardStateSelector';
import {resetSelectedTasks, setSelectedTask} from 'stores/actions/taskListStateAction';
import {generateCardGrouping} from 'lib/protocolHelper';
import {checkVolumeModalAndApplyIfOpened} from 'stores/business/virtualTrayBusiness';
import {ALLOWED_OPEN_CONTEXT_MENU_TASK_TYPES} from 'constants/cardStateEnum';
import TaskList from './TaskList';
import Card from './Card/Card';
import CardGroup from './Card/CardGroup';
import CardContextMenu from './Card/CardContextMenu';
import CardDropZone from './Card/CardDropZone';
import TaskDelay from './Task/TaskDelay';
import TaskConfirmation from './Task/TaskConfirmation';

import {
  setIgnoreTask,
  editGeneralTask,
  selectCard,
  deleteCard,
  rearrangeTaskItemList,
  removeCardTask,
  insertTask,
  checkInvalidTask,
  addCardGroup,
  ungroupCardGroup,
  rearrangeCardFromGroupCard,
  deleteMultipleCardsAndGroups,
  duplicateCardsAndGroups,
  setIgnoreAllCards,
  checkAndApplyGeneralTask,
  checkAndSetSelectedTipChannelByCardId,
  setMultipleTaskIsSelected,
  unselectAllTaskInCard,
  setMultipleTaskStateSelected,
  isTaskNumberSelected,
  isTaskIDSelected,
  getGroupIndexByCardId,
  refreshCardIgnoreStatus,
} from '../../../stores/business/taskListBusinessAction';
import {getSdRawTaskIds, setIgnoredAllSdRawTasks} from '../../../stores/business/taskListSerialDilutionBusiness';
import {backHandler, onBroadcastReuseTipChange} from '../../../stores/business/taskListBroadcastBusiness';

import {
  setTaskIsSelected,
  setCardName,
  setCardIsExpanded,
  setGroupIsSelected,
  setCardGroupName,
  setGroupIsExpanded,
} from '../../../stores/actions/cardStateAction';
import {setSdRawTaskIsSelected} from '../../../stores/actions/cardSerialDilutionStateAction';
import {showModal, hideModal} from '../../../stores/actions/modalStateAction';
import {showSnackbar} from '../../../stores/actions/appStateAction';
import {restoreTipCountForSingleCard, saveProtocol} from '../../../stores/business/protocolManagementBusiness';

import {
  DragDropType,
  CardType,
  ExecutionStatus,
  Feedback,
  TaskType,
  ProtocolEditorViewMode,
  RuntimeMode,
  SerialDilutionStep,
  KeyboardKey,
  SubCardType,
  TrayType,
  CardInvalidType,
  VirtualTrayModalType,
} from '../../../constants/dataEnum';

import {draggable, droppable} from '../../../lib/dragAndDrop';
import withIntl from '../../../lib/withIntl';

import {find, filterToArray, getObjectIdsInBetween} from '../../../lib/objectHelper';
import {useClickOutside, useStaticCallback} from '../../../lib/customHooks';

import {onPoolingReuseTipChange, poolingBackHandler} from '../../../stores/business/taskListPoolingBusiness';
import {removeUnusedPicklistBarcode} from '../../../stores/business/taskListPicklistBusiness';
import {globalAddUndoItem, globalUndoRedoUpdatePresentState} from '../../../stores/business/undoRedoBusinessAction';
import {
  setBroadcastTaskEditStep,
  setCustomTipCount,
  setIsEditMixing,
  setSerialDilutionEditStep,
  setOpenSdPreview,
  setPoolingTaskEditStep,
  setSelectedMenu,
  setSelectedTipChannel,
  setSimpleTransferEditedType,
} from '../../../stores/actions/protocolEditorAction';
import {showReuseTipsModal} from '../../../stores/business/modalStateBusinessAction';

import jsonCopy from '../../../lib/jsonCopy';
import {isUserOperator} from '../../../stores/business/userManagementBusiness';
import TaskContextMenu from './Task/TaskContextMenu';
import {getCardExecutionStatus, getTaskIdExecutionStatus} from '../../../stores/business/runtimeBusiness';
import TaskConsolidateTips from './Task/TaskConsolidateTips';
import {isCardVisible, isConsolidateTipsCardExist} from '../../../lib/cardAndTaskHelper';
import NormalizationTaskContainer from './Task/Normalization/NormalizationTaskContainer';
import SerialDilutionTasksContainer from './Task/SerialDilution/SerialDilutionTasksContainer';
import MixingTasksContainer from './Task/Mixing/MixingTasksContainer';
import SimpleTransferTasksContainer from './Task/SimpleTransfer/SimpleTransferTasksContainer';
// import getPoolingTaskExecutionStatus from './Card/Pooling/Tasks/PoolingTaskHelper';
import BroadcastTasksContainer from './Task/Broadcast/BroadcastTasksContainer';
import {disallowEditBroadcast, getBroadcastTaskExecutionStatus} from './Task/Broadcast/Tasks/BroadcastTaskHelper';
import PoolingTasksContainer from './Task/Pooling/PoolingTasksContainer';
import {disallowEditPooling, getPoolingTaskExecutionStatus} from './Task/Pooling/Tasks/PoolingTaskHelper';

const areCardEqual = (prevProps, nextProps) =>
  prevProps.isSelected === nextProps.isSelected &&
  prevProps.isExpanded === nextProps.isExpanded &&
  prevProps.isIgnored === nextProps.isIgnored &&
  prevProps.invalid === nextProps.invalid &&
  prevProps.isDragging === nextProps.isDragging &&
  prevProps.invalidTypes === nextProps.invalidTypes &&
  prevProps.tasks === nextProps.tasks &&
  prevProps.onHover === nextProps.onHover &&
  prevProps.cardGroup === nextProps.cardGroup &&
  prevProps.key === nextProps.key &&
  prevProps.runtimeStatus === nextProps.runtimeStatus &&
  prevProps.sdRawTasksSummary === nextProps.sdRawTasksSummary &&
  prevProps.executionStatus === nextProps.executionStatus &&
  prevProps.onProgressRecordCSVIDs === nextProps.onProgressRecordCSVIDs &&
  prevProps.protocolPoolingTaskEditStep === nextProps.protocolPoolingTaskEditStep &&
  prevProps.children === nextProps.children &&
  prevProps.onShowTaskContextMenu === nextProps.onShowTaskContextMenu;

const DropableCard = React.memo(droppable(Card), areCardEqual);
const DraggableAndDroppableCard = draggable(DropableCard);
const DraggableAndDroppableCardGroup = draggable(droppable(CardGroup));

const MAX_KEYCODE_COMBINATION = 1;

function mapStateToProps(state) {
  const {ADModalShown, broadcastTaskEditStep, poolingTaskEditStep, selectedMenu, simpleTransferEditedType} =
    state.protocolEditorState;
  const {show} = state.modalState;
  const {loading, isAnyModalShow} = state.appState;
  return {
    cardStateTasks: state.cardState.tasks,
    cardStateGroups: state.cardState.groups,
    labwareState: state.labwareState,
    runtimeStateCsvRecordID: state.runtimeState.csvRecordID,
    runtimeStateDoneRecordCSVIDs: state.runtimeState.doneRecordCSVIDs,
    runtimeStateOnProgressRecordCSVIDs: state.runtimeState.onProgressRecordCSVIDs,
    runtimeStateStatus: state.runtimeState.status,
    protocolConfig: state.configState.protocolConfig,
    protocolEditorStateADModalShown: ADModalShown,
    protocolBroadcastTaskEditStep: broadcastTaskEditStep,
    protocolPoolingTaskEditStep: poolingTaskEditStep,
    protocolEditorMode: selectedMenu,
    isModalShowing: show || isAnyModalShow,
    isLoadingShowing: loading.show,
    simpleTransferEditedType,
    protocolTrayType: state.protocolState.lastOpenedProtocol?.trayType,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    taskListBusinessSetIgnoreAllCards: bindActionCreators(setIgnoreAllCards, dispatch),
    taskListBusinessSetIgnoreCardTask: bindActionCreators(setIgnoreTask, dispatch),
    taskListBusinessEditGeneralTask: bindActionCreators(editGeneralTask, dispatch),
    taskListBusinessSelectCard: bindActionCreators(selectCard, dispatch),
    taskListBusinessDeleteCard: bindActionCreators(deleteCard, dispatch),
    taskListBusinessRearrangTaskItemList: bindActionCreators(rearrangeTaskItemList, dispatch),
    taskListBusinessRemoveCardTask: bindActionCreators(removeCardTask, dispatch),
    taskListBusinessInsertCardTask: bindActionCreators(insertTask, dispatch),
    taskListBusinessCheckInvalidTask: bindActionCreators(checkInvalidTask, dispatch),
    taskListBusinessAddCardGroup: bindActionCreators(addCardGroup, dispatch),
    taskListBusinessUngroupCardGroup: bindActionCreators(ungroupCardGroup, dispatch),
    taskListBusinessRearrangeCardFromGroupCard: bindActionCreators(rearrangeCardFromGroupCard, dispatch),
    taskListBusinessDeleteMultipleCards: bindActionCreators(deleteMultipleCardsAndGroups, dispatch),
    taskListSerialDilutionBusinessGetSdRawTaskIds: bindActionCreators(getSdRawTaskIds, dispatch),
    taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks: bindActionCreators(setIgnoredAllSdRawTasks, dispatch),
    taskListBroadcastBusinessBackHandler: bindActionCreators(backHandler, dispatch),
    taskListBroadcastBusinessOnBroadcastReuseTipChange: bindActionCreators(onBroadcastReuseTipChange, dispatch),
    tasklistPicklistBusinessRemoveUnusedBarcode: bindActionCreators(removeUnusedPicklistBarcode, dispatch),
    cardStateActionSetCardIsExpanded: bindActionCreators(setCardIsExpanded, dispatch),
    cardActionSetTaskIsSelected: bindActionCreators(setTaskIsSelected, dispatch),
    cardActionSetCardName: bindActionCreators(setCardName, dispatch),
    cardActionSetCardGroupName: bindActionCreators(setCardGroupName, dispatch),
    cardActionSetGroupIsExpanded: bindActionCreators(setGroupIsExpanded, dispatch),
    cardSerialDilutionActionSetSdRawTaskIsSelected: bindActionCreators(setSdRawTaskIsSelected, dispatch),
    modalStateActionShowModal: bindActionCreators(showModal, dispatch),
    modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
    appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
    protocolBusinessSaveProtocol: bindActionCreators(saveProtocol, dispatch),
    taskListPoolingBusinessBackHandler: bindActionCreators(poolingBackHandler, dispatch),
    taskListPoolingBusinessOnPoolingReuseTipChange: bindActionCreators(onPoolingReuseTipChange, dispatch),
    // taskListMixingBusinessSaveMixingStateBeforeEdit: bindActionCreators(saveMixingStateBeforeEdit, dispatch),
    undoRedoBusinessActionGlobalAddUndoItem: bindActionCreators(globalAddUndoItem, dispatch),
    undoRedoBusinessActionGlobalUndoRedoUpdatePresentState: bindActionCreators(
      globalUndoRedoUpdatePresentState,
      dispatch,
    ),
    protocolEditorStateActionSetSelectedTipChannel: bindActionCreators(setSelectedTipChannel, dispatch),
    protocolEditorStateActionSetCustomTipCount: bindActionCreators(setCustomTipCount, dispatch),
    protocolEditorStateActionSetSerialDilutionEditStep: bindActionCreators(setSerialDilutionEditStep, dispatch),
    protocolEditorStateActionSetOpenSdPreview: bindActionCreators(setOpenSdPreview, dispatch),
    protocolEditorStateActionSetBroadcastTaskEditStep: bindActionCreators(setBroadcastTaskEditStep, dispatch),
    protocolEditorStateActionSetPoolingTaskEditStep: bindActionCreators(setPoolingTaskEditStep, dispatch),
    protocolEditorStateActionSetIsEditMixing: bindActionCreators(setIsEditMixing, dispatch),
    protocolEditorStateActionSetSimpleTransferEditedType: bindActionCreators(setSimpleTransferEditedType, dispatch),
    setProtocolEditorMode: bindActionCreators(setSelectedMenu, dispatch),
    taskListBusinessDuplicateCardsAndGroups: bindActionCreators(duplicateCardsAndGroups, dispatch),
    cardStateActionSetGroupIsSelected: bindActionCreators(setGroupIsSelected, dispatch),
    modalStateBusinessAction: bindActionCreators(showReuseTipsModal, dispatch),
    userManagemenBusinessIsUserOperator: bindActionCreators(isUserOperator, dispatch),
  };
}

function TaskListContainer({
  intl,
  selectedCard,
  selectedTaskList,
  cardStateTasks,
  cardStateGroups,
  labwareState,
  runtimeMode,
  readOnly,
  runtimeStateCsvRecordID,
  runtimeStateOnProgressRecordCSVIDs,
  runtimeStateStatus,
  taskListBusinessSetIgnoreAllCards,
  taskListBusinessSelectCard,
  taskListBusinessDeleteCard,
  taskListBusinessRemoveCardTask,
  taskListBusinessCheckInvalidTask,
  taskListBusinessAddCardGroup,
  taskListBusinessUngroupCardGroup,
  taskListBusinessRearrangeCardFromGroupCard,
  taskListBusinessDeleteMultipleCards,
  taskListSerialDilutionBusinessGetSdRawTaskIds,
  taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks,
  taskListBroadcastBusinessBackHandler,
  taskListBroadcastBusinessOnBroadcastReuseTipChange,
  tasklistPicklistBusinessRemoveUnusedBarcode,
  cardStateActionSetCardIsExpanded,
  cardActionSetTaskIsSelected,
  cardSerialDilutionActionSetSdRawTaskIsSelected,
  cardActionSetCardName,
  cardActionSetCardGroupName,
  cardActionSetGroupIsExpanded,
  modalStateActionShowModal,
  modalStateActionHideModal,
  appStateActionShowSnackbar,
  protocolBusinessSaveProtocol,
  onCardClick,
  onTaskEdit,
  notifyDeleteCard,
  notifyDeleteTask,
  taskListPoolingBusinessBackHandler,
  taskListPoolingBusinessOnPoolingReuseTipChange,
  // taskListMixingBusinessSaveMixingStateBeforeEdit,
  undoRedoBusinessActionGlobalAddUndoItem,
  undoRedoBusinessActionGlobalUndoRedoUpdatePresentState,
  protocolConfig,
  protocolEditorStateADModalShown,
  protocolEditorStateActionSetSelectedTipChannel,
  protocolEditorStateActionSetCustomTipCount,
  protocolEditorStateActionSetSerialDilutionEditStep,
  protocolBroadcastTaskEditStep,
  protocolEditorStateActionSetBroadcastTaskEditStep,
  protocolPoolingTaskEditStep,
  protocolEditorStateActionSetPoolingTaskEditStep,
  protocolEditorStateActionSetIsEditMixing,
  protocolEditorMode,
  setProtocolEditorMode,
  highlightedTask,
  setHighlightedTask,
  refList,
  isModalShowing,
  isLoadingShowing,
  taskListBusinessDuplicateCardsAndGroups,
  cardStateActionSetGroupIsSelected,
  modalStateBusinessAction,
  userManagemenBusinessIsUserOperator,
  virtualTrayRef,
  protocolTrayType,
  editablePickTipExcludedRef,
  svmExcludedComponentRef,
  menuListRef,
}) {
  const [prevHighlightedtask, setPrevHighlightedtask] = useState(null);
  const [contextMenuTask, setContextMenuTask] = useState({
    anchorEl: null,
    contextMenu: null,
  });

  const selectedGroup = useRef([]);

  const cardRef = useRef(null);
  const expandedCardRef = useRef(null);
  const cardContexMenuRef = useRef(null);
  const cardStateTasksRef = useRef({});
  const cardStateCardsRef = useRef({});
  const cardStateGroupsRef = useRef({});
  const startingGroup = useRef(null);
  const keyCodePressedList = useRef([]);

  // will contain array filled with ref elements of each rendered cards
  const eachCardsRef = useRef([]);

  const getSelectedCards = () => Object.values(cardStateCardsRef.current).filter((c) => c.isSelected);
  const getSelectedGroups = () => Object.values(cardStateGroupsRef.current).filter((g) => g.isSelected);
  const getTasksInCard = (cardTasks) =>
    Object.values(_.orderBy(_.pick(cardStateTasks, cardTasks), ['number'], ['asc']));

  const dispatch = useDispatch();

  const shownModalType = useSelector(getVirtualTrayShownModalType);
  const cardStateCards = useSelector(getCards);
  const deckLabwareRefs = useSelector(getDeckLabwareRefs);
  const isAspirateDispenseVolumeModalShown = useSelector(getIsAspirateDispenseModalShown);

  const deleteModalShowing = shownModalType === VirtualTrayModalType.DELETE_CONFIRMATION;
  const isReadOnly = readOnly || userManagemenBusinessIsUserOperator();

  const onGetCardExecutionStatus = (cardId) =>
    runtimeMode ? dispatch(getCardExecutionStatus(cardStateCardsRef.current[cardId])) : ExecutionStatus.NONE;

  useEffect(() => {
    cardStateGroupsRef.current = cardStateGroups;
    const selectedGroups = getSelectedGroups();
    selectedGroup.current = selectedGroups.map((g) => g.ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardStateGroups]);

  useEffect(() => {
    cardStateTasksRef.current = cardStateTasks;
    // setIsCardExpanded(true);
  }, [cardStateTasks]);

  const groupCard = useMemo(
    () => generateCardGrouping(cardStateCards, cardStateGroups),
    [cardStateCards, cardStateGroups],
  );

  useEffect(() => {
    cardStateCardsRef.current = cardStateCards;
    const selectedCards = getSelectedCards();

    if (selectedCards.length === 1) {
      const [groupIndex, cardIndex] = dispatch(getGroupIndexByCardId(selectedCards[0].ID, groupCard));
      startingGroup.current = {
        ...selectedCards[0],
        groupIndex,
        cardIndex,
      };
    }
  }, [cardStateCards, groupCard, dispatch]);

  function refreshTaskContext(cardTaskIds, isEdit = false) {
    if (runtimeMode) return;

    dispatch(checkAndSetSelectedTipChannelByCardId(selectedCard.ID, isEdit, cardTaskIds));
  }

  const onCheckedCardChange = async (cardID, cardType, checked) => {
    cardContexMenuRef.current.hide();

    if (cardType === CardType.SERIAL_DILUTION) {
      await taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks(cardID, !checked);
      undoRedoBusinessActionGlobalAddUndoItem();
      return;
    }

    taskListBusinessSetIgnoreAllCards([cardID], !checked);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const setStartingGroup = (groupIndex, cardIndex) => {
    if (typeof groupIndex !== 'number' && !cardIndex) {
      startingGroup.current = null;
      return;
    }

    const selected = groupCard[groupIndex];
    if (selected.card) {
      startingGroup.current = {
        ...selected.card,
        groupIndex,
        cardIndex,
      };
      return;
    }

    if (cardIndex !== '#' && cardIndex) {
      const currentSelectedCard = {
        ...selected.cards[cardIndex],
        groupIndex,
        cardIndex,
      };
      startingGroup.current = currentSelectedCard;
      return;
    }

    startingGroup.current = {
      ...selected,
      groupIndex,
      cardIndex,
    };
  };

  const getStartingAndEndingCard = (startGroup, groupIndex, cardIndex) => {
    const currentGroup = groupCard[groupIndex];
    const currentGroupCardList = currentGroup.cards;

    let startingCard = jsonCopy(startGroup);
    if (!startingCard && cardIndex !== '#') {
      startingCard = {
        ...currentGroupCardList[cardIndex],
        groupIndex,
        cardIndex,
      };
    } else if (!startingCard) {
      startingCard = {
        ...currentGroupCardList[0],
        groupIndex,
        cardIndex,
      };
    } else if (startingCard?.cards && startingCard.groupIndex <= groupIndex) {
      startingCard = {
        ...startGroup.cards[0],
        groupIndex: startGroup.groupIndex,
        cardIndex: startGroup.cardIndex,
      };
    } else if (startingCard?.cards && startingCard.groupIndex > groupIndex) {
      startingCard = {
        ...startGroup.cards[startGroup.cards.length - 1],
        groupIndex: startGroup.groupIndex,
        cardIndex: startGroup.cardIndex,
      };
    }

    let endingCard;
    if (cardIndex === '#') {
      // clicked on card outside group (cardIndex is #)
      endingCard = {...currentGroup.card, groupIndex, cardIndex};
    } else if (cardIndex !== '#' && typeof cardIndex === 'number') {
      // clicked on card inside group (cardIndex exist and not #)
      endingCard = {...currentGroupCardList[cardIndex], groupIndex, cardIndex};
    } else if (!cardIndex && startingCard.groupIndex <= groupIndex) {
      // clicked on group (cardIndex is null)
      endingCard = {
        ...currentGroupCardList[currentGroupCardList.length - 1],
        groupIndex,
        cardIndex,
      };
    } else {
      // clicked on group (cardIndex is null)
      endingCard = {...currentGroupCardList[0], groupIndex, cardIndex};
    }

    return [startingCard, endingCard];
  };

  const getGroupIdsBetween = (startId, endId, objectList) => {
    let startIndex = startId;
    let endIndex = endId;

    if (startIndex > endIndex) {
      [startIndex, endIndex] = [endIndex, startIndex];
    }

    const selectedIds = [];
    for (let i = startIndex; i <= endIndex; i += 1) {
      const currentCardID = objectList[i]?.ID;
      if (currentCardID !== null && currentCardID !== undefined) {
        selectedIds.push(currentCardID);
      }
    }

    return selectedIds;
  };

  const onShiftSelect = (groupIndex, cardIndex) => {
    const unselectAllCardsAndGroup = () => {
      taskListBusinessSelectCard();
      cardStateActionSetGroupIsSelected();
    };

    if (!groupCard.length) {
      return;
    }

    unselectAllCardsAndGroup();

    // get starting and ending card
    const [startingCard, endingCard] = getStartingAndEndingCard(startingGroup.current, groupIndex, cardIndex);

    const cardIdsToSelect = getObjectIdsInBetween(startingCard.ID, endingCard.ID, cardStateCards);
    if (cardIdsToSelect.length) taskListBusinessSelectCard(cardIdsToSelect, false);

    const groupIdsToSelect = getGroupIdsBetween(startingGroup.current?.groupIndex, groupIndex, groupCard);

    if (startingCard?.cardIndex !== null && startingCard?.cardIndex !== undefined && startingCard?.cardIndex !== '#') {
      const groupIdToRemove = groupCard[startingCard.groupIndex]?.ID;
      const groupIndexToRemove = groupIdsToSelect.indexOf(groupIdToRemove);
      groupIdsToSelect.splice(groupIndexToRemove, 1);
    }

    if (endingCard?.cardIndex !== null && endingCard?.cardIndex !== undefined && endingCard?.cardIndex !== '#') {
      const groupIdToRemove = groupCard[endingCard.groupIndex]?.ID;
      const groupIndexToRemove = groupIdsToSelect.indexOf(groupIdToRemove);
      groupIdsToSelect.splice(groupIndexToRemove, 1);
    }

    if (groupIdsToSelect.length) cardStateActionSetGroupIsSelected(groupIdsToSelect, false);
  };

  const onDuplicateCard = useStaticCallback(() => {
    onSelectTask();
    if (protocolEditorMode === ProtocolEditorViewMode.SIMPLE_TRANSFER) {
      taskListBusinessDuplicateCardsAndGroups();
    }
  });

  const getInsertTask = () => {
    const tasksStateArr = Object.values(cardStateTasks);
    const lastTask = tasksStateArr[tasksStateArr.length - 1];
    return lastTask?.type === TaskType.INSERT_TASK ? lastTask : null;
  };

  const handleMouseDownCard = useStaticCallback(
    async (evt, cardId, cardType, groupIndex, cardIndex, isContexMenu = false) => {
      if (!isContexMenu) {
        cardContexMenuRef.current.hide();
        setContextMenuTask({anchorEl: null, contextMenu: null});
      }

      const newSelectedCard = cardStateCards[cardId];

      // prevent to select another card if is in serial dilution or broadcast or pooling mode
      if (
        protocolEditorMode === ProtocolEditorViewMode.SERIAL_DILUTION ||
        protocolEditorMode === ProtocolEditorViewMode.NORMALIZATION ||
        protocolEditorMode === ProtocolEditorViewMode.BROADCAST ||
        protocolEditorMode === ProtocolEditorViewMode.POOLING ||
        protocolEditorMode === ProtocolEditorViewMode.MIXING
      ) {
        return;
      }

      dispatch(checkVolumeModalAndApplyIfOpened());

      const prevSelecteCard = selectedCard;
      if (!runtimeMode && evt?.shiftKey) {
        batch(async () => {
          if (prevSelecteCard.isExpanded) {
            await cardStateActionSetCardIsExpanded(prevSelecteCard.ID, false);
          }

          if (_.isEmpty(startingGroup.current)) {
            setStartingGroup(groupIndex, cardIndex);
          }
          onShiftSelect(groupIndex, cardIndex);
          protocolEditorStateActionSetSelectedTipChannel(null);
          protocolEditorStateActionSetCustomTipCount(null);
        });
        return;
      }

      if (!runtimeMode && evt?.ctrlKey) {
        batch(async () => {
          const status = !newSelectedCard.isSelected;
          await cardStateActionSetCardIsExpanded(prevSelecteCard.ID, false);
          taskListBusinessSelectCard(cardId, false, status);
          protocolEditorStateActionSetSelectedTipChannel(null);
          protocolEditorStateActionSetCustomTipCount(null);
          setStartingGroup(groupIndex, cardIndex);
        });
        return;
      }

      // unselect card group
      cardStateActionSetGroupIsSelected();
      onCardClick(cardId, cardType);
      const selectedCards = getSelectedCards();

      if (prevSelecteCard.ID !== cardId || selectedCards.length > 1) {
        taskListBusinessSelectCard(cardId);
        refreshTaskContext(newSelectedCard.tasks);
        setStartingGroup(groupIndex, cardIndex);
      } else {
        return;
      }

      if (
        !runtimeMode &&
        (cardType === CardType.SERIAL_DILUTION ||
          cardType === CardType.NORMALIZATION ||
          cardType === CardType.BROADCAST ||
          cardType === CardType.POOLING)
      ) {
        protocolEditorStateActionSetSelectedTipChannel('');
        protocolEditorStateActionSetCustomTipCount(null);
      }

      if (prevSelecteCard.type === CardType.SIMPLE_TRANSFER) {
        const hasEjectTipTask = !!find(cardStateTasks, {
          ID: prevSelecteCard?.tasks,
          type: TaskType.EJECT_TIP,
          isIgnored: false || undefined || null || '',
        });

        if (!hasEjectTipTask && !runtimeMode) {
          const isHighVolume = protocolTrayType === TrayType.HIGH_VOLUME_CADDY;
          taskListBusinessCheckInvalidTask(Number(prevSelecteCard.ID), true, false, false, false, isHighVolume);
        }
      }

      if (runtimeMode) {
        return;
      }

      protocolBusinessSaveProtocol();
    },
  );

  const handleMouseUpCard = useStaticCallback((evt, cardId) => {
    if (
      protocolEditorMode === ProtocolEditorViewMode.SERIAL_DILUTION ||
      protocolEditorMode === ProtocolEditorViewMode.NORMALIZATION ||
      protocolEditorMode === ProtocolEditorViewMode.BROADCAST ||
      protocolEditorMode === ProtocolEditorViewMode.POOLING ||
      protocolEditorMode === ProtocolEditorViewMode.MIXING
    ) {
      return;
    }
    if (!selectedCard.isExpanded && !evt?.ctrlKey && !evt?.shiftKey) {
      cardStateActionSetCardIsExpanded(cardId, false);
    }
  });

  const handMouseDownExpandCard = (evt, cardID, groupIndex, cardIndex) => {
    dispatch(checkVolumeModalAndApplyIfOpened());

    if (!selectedCard.isInsertTask) {
      const newSelectedCard = cardStateCards[cardID];
      handleMouseDownCard(evt, cardID, newSelectedCard.type, groupIndex, cardIndex);
    }
  };

  const handMouseUpExpandCard = (evt, cardID, isExpanded) => {
    // prevent expand card when multiple select mode
    const selectedCards = getSelectedCards();
    if (evt.ctrlKey || evt.shiftKey || selectedCards.length > 1) {
      return;
    }

    if (selectedCard.isInsertTask) {
      const newInsertTask = getInsertTask();
      const newSelectedCard = cardStateCards[cardID];

      if (protocolEditorStateADModalShown) {
        virtualTrayRef.current.applyAspirateDispenseModal();
        return;
      }

      onDeleteTask(newInsertTask.ID, selectedCard.ID, false, false);
      handleMouseDownCard(evt, cardID, newSelectedCard.type);
    }
    // prevent to select another card if is in serial dilution or broadcast or pooling mode
    if (
      protocolEditorMode === ProtocolEditorViewMode.SERIAL_DILUTION ||
      protocolEditorMode === ProtocolEditorViewMode.NORMALIZATION ||
      protocolEditorMode === ProtocolEditorViewMode.BROADCAST ||
      protocolEditorMode === ProtocolEditorViewMode.POOLING ||
      protocolEditorMode === ProtocolEditorViewMode.MIXING
    ) {
      return;
    }
    cardStateActionSetCardIsExpanded(cardID, isExpanded);
  };

  const deleteCardValidation = (cardIDs) => {
    let blankTaskCount = 0;
    let needConfirmation = true;

    const taskIDs = Object.values(cardStateCardsRef.current)
      .filter(
        (card) => cardIDs.includes(card.ID) && (card.type === CardType.CONFIRMATION || card.type === CardType.DELAY),
      )
      .map((card) => card.tasks)
      .flat();

    if (!taskIDs.length) {
      return needConfirmation;
    }

    const tasks = Object.values(cardStateTasks).filter((task) => taskIDs.includes(task.ID));

    tasks.forEach((task) => {
      if (
        (task.type === TaskType.DELAY && task.delayTime === 0) ||
        ((task.type === TaskType.USER_CONFIRMATION || task.type === TaskType.USER_CONFIRMATION_EJECT) &&
          task.message === '')
      ) {
        blankTaskCount += 1;
      }
    });

    if (blankTaskCount === tasks.length) {
      needConfirmation = false;
    }

    return needConfirmation;
  };

  const showDeleteConfirmationModal = (callback) => {
    dispatch(setVirtualTrayShownModal(VirtualTrayModalType.DELETE_CONFIRMATION));

    modalStateActionShowModal(
      intl.formatMessage({id: 'protocolEditor.taskList.removeCard.modal.title'}),
      intl.formatMessage({id: 'protocolEditor.taskList.removeCard.modal.content'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(resetVirtualTrayShownModal());
            modalStateActionHideModal();
            callback();
          },
          text: intl.formatMessage({id: 'common.modal.action.yes'}),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(resetVirtualTrayShownModal());
            modalStateActionHideModal();
          },
          text: intl.formatMessage({id: 'common.modal.action.no'}),
        },
      ],
      () => modalStateActionHideModal(),
      '',
      true,
    );
  };

  const onDeleteCard = (cardID, cardType, isPicklist) => {
    if (
      (protocolEditorMode === ProtocolEditorViewMode.SERIAL_DILUTION ||
        protocolEditorMode === ProtocolEditorViewMode.BROADCAST ||
        protocolEditorMode === ProtocolEditorViewMode.POOLING) &&
      selectedCard.ID !== cardID
    ) {
      return;
    }

    const deleteProcess = () => {
      protocolEditorStateActionSetSelectedTipChannel('');
      protocolEditorStateActionSetCustomTipCount(null);

      batch(async () => {
        if (
          protocolEditorMode === ProtocolEditorViewMode.SERIAL_DILUTION ||
          protocolEditorMode === ProtocolEditorViewMode.BROADCAST ||
          protocolEditorMode === ProtocolEditorViewMode.POOLING ||
          protocolEditorMode === ProtocolEditorViewMode.MIXING
        ) {
          setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
        }

        protocolEditorStateActionSetSerialDilutionEditStep(0);
        protocolEditorStateActionSetBroadcastTaskEditStep(null);
        protocolEditorStateActionSetPoolingTaskEditStep(null);
        protocolEditorStateActionSetIsEditMixing(false);

        await taskListBusinessDeleteCard(cardID, true);
        if (isPicklist) {
          tasklistPicklistBusinessRemoveUnusedBarcode(true);
        }
        undoRedoBusinessActionGlobalAddUndoItem();
        setStartingGroup();

        appStateActionShowSnackbar(
          Feedback.SUCCESS,
          intl.formatMessage({
            id: 'protocolEditor.taskList.removeCard.notification.success',
          }),
        );
      });

      notifyDeleteCard(cardType);
    };

    if (deleteCardValidation([cardID])) {
      showDeleteConfirmationModal(deleteProcess);
    } else {
      deleteProcess();
    }
  };

  function deleteTask(taskId, cardId, isPicklist) {
    batch(async () => {
      modalStateActionHideModal();

      if (cardStateCards[cardId].tasks.length <= 1) {
        await taskListBusinessDeleteCard(cardId, true);
        if (protocolEditorMode === ProtocolEditorViewMode.MIXING) {
          setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
        }
      } else {
        await taskListBusinessRemoveCardTask(cardId, taskId, true);
        dispatch(refreshCardIgnoreStatus(cardId));
      }
      if (isPicklist) {
        await tasklistPicklistBusinessRemoveUnusedBarcode(true);
      }
      undoRedoBusinessActionGlobalAddUndoItem();
      appStateActionShowSnackbar(
        Feedback.SUCCESS,
        intl.formatMessage({
          id: 'protocolEditor.taskList.removeTask.notification.success',
        }),
      );
    });

    if (protocolEditorMode === ProtocolEditorViewMode.SIMPLE_TRANSFER) {
      const card = cardStateCards[cardId];

      if (card && card.tasks?.length) {
        const taskIdsLeft = card.tasks.filter((Id) => Id !== taskId);
        refreshTaskContext(taskIdsLeft);
      }
    }
    dispatch(restoreTipCountForSingleCard(cardId));
  }

  async function onDeleteTask(taskId, cardId, isPicklist, showConfirmationModal = true) {
    if (!showConfirmationModal) {
      if (!protocolEditorStateADModalShown) deleteTask(taskId, cardId, isPicklist);
      return;
    }

    modalStateActionShowModal(
      intl.formatMessage({id: 'protocolEditor.taskList.removeTask.modal.title'}),
      intl.formatMessage({id: 'protocolEditor.taskList.removeTask.modal.content'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => deleteTask(taskId, cardId, isPicklist),
          text: intl.formatMessage({id: 'protocolEditor.taskList.removeTask.modal.confirmButton'}),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => modalStateActionHideModal(),
          text: intl.formatMessage({id: 'protocolEditor.taskList.removeTask.modal.cancelButton'}),
        },
      ],
      () => modalStateActionHideModal(),
      '',
      true,
    );
  }

  const isDisableCardDropZone = (item, groupIndex, subIndex, isFirst) => {
    const [draggedIndexstr, draggedSubIndexstr, , , draggedIsFirst] = item.ID.split('-');
    const draggedIndex = parseInt(draggedIndexstr, 10);
    const draggedSubIndex = draggedSubIndexstr !== '#' ? parseInt(draggedSubIndexstr, 10) : '#';

    const selectedCards = getSelectedCards();
    const selectedGroups = getSelectedGroups();
    const isMultipleSelectedCardGroup = selectedCards.length + selectedGroups.length > 1;

    return (
      !isMultipleSelectedCardGroup &&
      ((draggedSubIndex === '#' &&
        subIndex === '#' &&
        (!isFirst || draggedIsFirst) && // outside group
        (draggedIndex === groupIndex || draggedIndex - groupIndex === 1)) ||
        (draggedSubIndex !== '#' &&
          groupIndex === draggedIndex &&
          (!isFirst || draggedIsFirst) && // inside group
          ((draggedSubIndex === subIndex && !isFirst) || draggedSubIndex - subIndex === 1)))
    );

    // return false;
  };

  const isDisableCardDrop = (item, card) => {
    const [, , , draggedIDstr] = item.ID.split('-');
    const draggedID = parseInt(draggedIDstr, 10);

    return draggedID === card.ID || (card.groupID !== null && card.groupID !== undefined);
  };

  const onCardDrop = useStaticCallback((item, itemID) => {
    const [, , draggedType, draggedIDstr] = item.ID.split('-');
    const draggedID = parseInt(draggedIDstr, 10);
    const [, , , droppedIDstr] = itemID.split('-');
    const droppedID = parseInt(droppedIDstr, 10);

    if (draggedID === droppedID || draggedType === 'group') return;

    const selectedCards = getSelectedCards();
    const selectedCardIds = selectedCards.map((c) => c.ID);

    if (!selectedCardIds.includes(droppedID)) {
      selectedCardIds.push(droppedID);
    }

    taskListBusinessAddCardGroup(selectedCardIds.sort());
  });

  const onDragCard = (itemID) => {
    const [, , , draggedIDstr] = itemID.split('-');
    const draggedID = parseInt(draggedIDstr, 10);

    const selectedCards = getSelectedCards();
    const selectedCardIds = selectedCards.map((c) => c.ID);

    if (!selectedCardIds.includes(draggedID)) {
      const draggedCard = cardStateCards[draggedID];
      handleMouseDownCard(null, draggedID, draggedCard.type);
    }
  };

  const getPreviousID = (cardID, cardIDs) => {
    const cardKeys = Object.keys(cardStateCards);
    const index = cardKeys.findIndex((ID) => parseInt(ID, 10) === cardID);
    const prevCardIDstr = cardKeys[index - 1];

    if (prevCardIDstr !== undefined) {
      const prevCardID = parseInt(prevCardIDstr, 10);
      if (cardIDs.includes(prevCardID)) {
        return getPreviousID(prevCardID, cardIDs);
      }

      return prevCardID;
    }

    return -1;
  };

  const onRearrangeCardDrop = useStaticCallback((item, itemID, droppedGroupID = null) => {
    const selectedCards = getSelectedCards();
    const selectedCardIds = selectedCards.map((c) => c.ID);

    const [, , draggedType, draggedIDstr] = item.ID.split('-');
    const draggedID = parseInt(draggedIDstr, 10);
    const [, , droppedType, droppedIDstr, droppedIsFirst] = itemID.split('-');
    const droppedID = parseInt(droppedIDstr, 10);

    if (draggedID === droppedID && draggedType === droppedType && selectedCardIds.length === 1) return;
    handleMouseUpCard(null, draggedID);

    const cardIDs = selectedCardIds.length ? selectedCardIds : [draggedID];
    let startRearrange = droppedIsFirst ? -1 : droppedID;

    if (!droppedIsFirst) {
      if (droppedType === 'card' && cardIDs.includes(droppedID)) {
        startRearrange = getPreviousID(droppedID, cardIDs);
      } else if (droppedType === 'group') {
        const group = cardStateGroups[droppedID];
        const lastCardId = group.cards[group.cards.length - 1];
        if (cardIDs.includes(lastCardId)) {
          startRearrange = getPreviousID(lastCardId, cardIDs);
        } else {
          startRearrange = lastCardId;
        }
      }
    } else if (droppedIsFirst) {
      const index = groupCard.findIndex((group) => group.ID === droppedGroupID);
      const prevGroup = groupCard[index - 1];
      if (prevGroup) {
        const lastCardId = prevGroup?.card?.ID ?? prevGroup.cards[prevGroup.cards.length - 1].ID;
        if (cardIDs.includes(lastCardId)) {
          startRearrange = getPreviousID(lastCardId, cardIDs);
        } else {
          startRearrange = lastCardId;
        }
      }
    }

    const isAddGroup = draggedType === 'card' && droppedGroupID !== null && droppedGroupID !== undefined;
    taskListBusinessRearrangeCardFromGroupCard(cardIDs, startRearrange, droppedGroupID, isAddGroup);
  });

  function getTaskExecutionStatus(taskId) {
    if (!runtimeMode) return ExecutionStatus.NONE;

    return dispatch(getTaskIdExecutionStatus(taskId));
  }

  function getConsolidateTipsExecutionStatus() {
    if (!runtimeMode && runtimeStateStatus === RuntimeMode.ABORTED) {
      return ExecutionStatus.NONE;
    }

    if (runtimeStateStatus === RuntimeMode.FINISH) {
      return ExecutionStatus.EXECUTED;
    }

    return ExecutionStatus.EXECUTING;
  }

  // #region highlight task

  const onSelectTaskWithObjectProps = ({
    taskId = null,
    taskNumber = null,
    cardType = null,
    taskList = null,
    cardID = null,
    isHideTaskContextMenu = true,
    bypassValidityChecking = false,
  } = {}) => {
    onSelectTask(taskId, cardType, taskList, cardID, isHideTaskContextMenu, {bypassValidityChecking, taskNumber});
  };

  const onSelectTask = (
    ID,
    cardType,
    taskList = null,
    cardID = null,
    isHideTaskContextMenu = true,
    opt = {bypassValidityChecking: false, taskNumber: null},
  ) => {
    const {bypassValidityChecking, taskNumber} = opt || {};
    const isSelectingSameTask =
      highlightedTask?.ID === ID || taskNumber
        ? dispatch(isTaskNumberSelected(taskNumber, cardID))
        : dispatch(isTaskIDSelected(ID));

    if (
      (runtimeMode ||
        protocolEditorStateADModalShown ||
        (isAspirateDispenseVolumeModalShown && (isSelectingSameTask || _.isNil(ID))) ||
        protocolEditorMode !== ProtocolEditorViewMode.SIMPLE_TRANSFER) &&
      !bypassValidityChecking
    ) {
      return;
    }

    if (isSelectingSameTask) {
      if (!_.isNil(ID)) {
        onSelectTask();
      }

      return;
    }

    const selectedTaskIds = taskList?.length ? taskList.map((task) => task.ID) : [ID];
    let cardToUnselect = selectedCard;

    if (highlightedTask) {
      setPrevHighlightedtask(highlightedTask);
      cardToUnselect = cardStateCards[highlightedTask.cardID];
    }

    if (
      cardType === CardType.SERIAL_DILUTION ||
      cardType === CardType.NORMALIZATION ||
      cardType === CardType.BROADCAST ||
      cardType === CardType.POOLING
    ) {
      if (highlightedTask?.ID !== ID || highlightedTask?.ID !== taskNumber) {
        // Flow for serial dilution is separated right now because there's a future plan to select tasks explicitly by its number and card ID instead of the task ID
        // That plan is adopted to only serial dilution cards as of now.
        if (cardType === CardType.SERIAL_DILUTION) {
          batch(() => {
            setHighlightedTask({ID: taskNumber, type: cardType, taskList, cardID});
            dispatch(setMultipleTaskStateSelected(taskList, selectedCard.ID));
            dispatch(unselectAllTaskInCard(cardToUnselect));

            const selectedProcessedTaskIds = taskList.map((task) => task.processedTaskId);
            dispatch(setMultipleTaskIsSelected(selectedProcessedTaskIds, true));
          });
        } else {
          batch(() => {
            // the ID declared here meant the task number
            setHighlightedTask({ID, type: cardType, taskList, cardID});
            dispatch(setMultipleTaskStateSelected(taskList, selectedCard.ID));
            dispatch(unselectAllTaskInCard(cardToUnselect));
            dispatch(setMultipleTaskIsSelected(selectedTaskIds, true));
          });
        }
      } else {
        batch(() => {
          setHighlightedTask(null);
          dispatch(unselectAllTaskInCard(cardToUnselect));
          dispatch(resetSelectedTasks());
        });
      }

      return;
    }

    if (highlightedTask?.ID === ID && !isHideTaskContextMenu) {
      return;
    }

    if (!_.isNil(ID) && highlightedTask?.ID !== ID) {
      batch(() => {
        setHighlightedTask({ID, type: cardType, taskList, cardID});
        dispatch(setSelectedTask({taskID: ID, taskNumber, cardID: selectedCard.ID}));
        dispatch(unselectAllTaskInCard(cardToUnselect));
        dispatch(setMultipleTaskIsSelected(selectedTaskIds, true));
      });
    } else {
      batch(() => {
        setHighlightedTask(null);
        dispatch(unselectAllTaskInCard(cardToUnselect));
        dispatch(resetSelectedTasks());
      });
      setContextMenuTask({anchorEl: null, contextMenu: null});
    }
  };

  useEffect(() => {
    if (prevHighlightedtask) {
      if (prevHighlightedtask.taskList && prevHighlightedtask?.type === CardType.SERIAL_DILUTION) {
        prevHighlightedtask.taskList.forEach((item) => {
          if (item) {
            cardSerialDilutionActionSetSdRawTaskIsSelected(prevHighlightedtask.cardID, item.ID, false);
          }
        });
      } else if (
        prevHighlightedtask.taskList &&
        (prevHighlightedtask?.type === CardType.NORMALIZATION ||
          prevHighlightedtask?.type === CardType.BROADCAST ||
          prevHighlightedtask?.type === CardType.POOLING)
      ) {
        prevHighlightedtask.taskList.forEach((item) => {
          if (item) {
            cardActionSetTaskIsSelected(item.ID, false);
          }
        });
      }
      // } else {
      //   cardActionSetTaskIsSelected(prevHighlightedtask?.ID, false);
      // }
      setPrevHighlightedtask(null);
    }

    if (highlightedTask) {
      if (highlightedTask.taskList && highlightedTask.type === CardType.SERIAL_DILUTION) {
        highlightedTask.taskList.forEach((item) => {
          if (item) {
            cardSerialDilutionActionSetSdRawTaskIsSelected(highlightedTask.cardID, item.ID, true);
          }
        });
      } else if (
        highlightedTask.taskList &&
        (highlightedTask.type === CardType.NORMALIZATION ||
          highlightedTask.type === CardType.BROADCAST ||
          highlightedTask.type === CardType.POOLING)
      ) {
        highlightedTask.taskList.forEach((item) => {
          if (item) {
            cardActionSetTaskIsSelected(item.ID, true);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedTask]);

  const getSdRawTaskList = (ID, card) => {
    const [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetIds, taskDiluentTargetIds] =
      taskListSerialDilutionBusinessGetSdRawTaskIds(card.ID);

    let taskList = null;

    switch (ID) {
      case SerialDilutionStep.SET_SAMPLE_TARGET:
        taskList = filterToArray(card.sdRawTasks, {ID: taskSampleTargetIds});
        break;
      case SerialDilutionStep.SET_SAMPLE_SOURCE:
        taskList = _.isNumber(taskSampleSourceId) ? [card.sdRawTasks[taskSampleSourceId]] : null;
        break;
      case SerialDilutionStep.SET_DILUENT_SOURCE:
        taskList = _.isNumber(taskDiluentSourceId) ? [card.sdRawTasks[taskDiluentSourceId]] : null;
        break;
      case SerialDilutionStep.SET_DILUENT_TARGET:
        taskList = filterToArray(card.sdRawTasks, {ID: taskDiluentTargetIds});
        break;
      default:
        break;
    }

    return taskList;
  };

  const getFirstSdStep = (card) => {
    const [taskDiluentSourceId, taskSampleSourceId, taskSampleTargetId] = taskListSerialDilutionBusinessGetSdRawTaskIds(
      card.ID,
    );

    if (taskDiluentSourceId) {
      return SerialDilutionStep.SET_DILUENT_SOURCE;
    }
    if (taskSampleSourceId) {
      return SerialDilutionStep.SET_SAMPLE_SOURCE;
    }
    if (taskSampleTargetId) {
      return SerialDilutionStep.SET_SAMPLE_TARGET;
    }

    return SerialDilutionStep.SET_DILUENT_TARGET;
  };

  const getNextSdStep = (card, ID) => {
    const sdTasks = taskListSerialDilutionBusinessGetSdRawTaskIds(card.ID);

    let nextStep;

    Object.values(SerialDilutionStep).every((sdStep) => {
      nextStep = sdStep;
      if (sdStep > 0 && sdStep > ID && sdTasks[sdStep - 1]) {
        return false;
      }
      return true;
    });

    return nextStep;
  };

  const getPrevSdStep = (card, ID) => {
    const sdTasks = taskListSerialDilutionBusinessGetSdRawTaskIds(card.ID);

    let nextStep;

    Object.values(SerialDilutionStep)
      .sort((a, b) => b - a)
      .every((sdStep) => {
        nextStep = sdStep;
        if (sdStep > 0 && sdStep < ID && sdTasks[sdStep - 1]) {
          return false;
        }
        return true;
      });

    return nextStep;
  };

  const getNormalizationTasks = (number, card) => {
    const tasks = Object.values(_.orderBy(_.pick(cardStateTasks, card.tasks), ['number'], ['asc']));

    const taskAspirate = tasks.filter((e) => e.type === TaskType.ASPIRATE);

    const taskDispenses = tasks.filter((e) => e.type === TaskType.DISPENSE);
    if (number === 1) {
      return taskDispenses;
    }

    return taskAspirate;
  };

  const getBroadcastTasks = (number, card) => {
    const tasks = Object.values(_.orderBy(_.pick(cardStateTasks, card.tasks), ['number'], ['asc']));

    const taskAspirate = tasks.filter((e) => e.type === TaskType.ASPIRATE);

    const taskDispenses = tasks.filter((e) => e.type === TaskType.DISPENSE);

    if (number === 1) {
      return taskAspirate;
    }

    return taskDispenses;
  };

  const getPoolingTasks = (number, card) => {
    const tasks = Object.values(_.orderBy(_.pick(cardStateTasks, card.tasks), ['number'], ['asc']));

    const taskAspirate = tasks.filter((e) => e.type === TaskType.ASPIRATE);

    const taskDispenses = tasks.filter((e) => e.type === TaskType.DISPENSE);

    if (number === 1 || number === 2) {
      return taskAspirate;
    }

    return taskDispenses;
  };

  const getTaskFromCardState = (taskType, card) => {
    const tasks = Object.values(_.orderBy(_.pick(cardStateTasks, card.tasks), ['number'], ['asc'])).filter(
      (e) => e.type === taskType,
    );

    return tasks;
  };

  const onArrowDownPress = () => {
    if (highlightedTask) {
      if (
        highlightedTask.type === CardType.SERIAL_DILUTION &&
        highlightedTask.ID !== SerialDilutionStep.SET_DILUENT_TARGET
      ) {
        const nextStep = getNextSdStep(selectedCard, highlightedTask.ID);
        const taskList = getSdRawTaskList(nextStep, selectedCard);
        onSelectTask(nextStep, highlightedTask.type, taskList, selectedCard.ID, true, {
          taskNumber: nextStep,
          bypassValidityChecking: false,
        });
        return;
      }

      if (highlightedTask.type === CardType.NORMALIZATION && highlightedTask.ID !== 3) {
        const taskList = getPoolingTasks(highlightedTask.ID + 1, selectedCard);
        onSelectTask(highlightedTask.ID + 1, highlightedTask.type, taskList, selectedCard.ID);
        return;
      }

      if (highlightedTask.type === CardType.BROADCAST && highlightedTask.ID !== 3) {
        const taskList = getBroadcastTasks(highlightedTask.ID + 1, selectedCard);
        onSelectTask(highlightedTask.ID + 1, highlightedTask.type, taskList, selectedCard.ID);
        return;
      }

      if (highlightedTask.type === CardType.POOLING && highlightedTask.ID !== 3) {
        const taskList = getPoolingTasks(highlightedTask.ID + 1, selectedCard);
        onSelectTask(highlightedTask.ID + 1, highlightedTask.type, taskList, selectedCard.ID);
        return;
      }

      if (
        highlightedTask.type !== CardType.NORMALIZATION &&
        highlightedTask.type !== CardType.SERIAL_DILUTION &&
        highlightedTask.type !== CardType.BROADCAST &&
        highlightedTask.type !== CardType.POOLING
      ) {
        const taskIds = selectedCard.tasks;
        const currentSelectedTaskIndex = taskIds.findIndex((e) => e === highlightedTask.ID);

        if (currentSelectedTaskIndex !== taskIds.length - 1) {
          onSelectTask(taskIds[currentSelectedTaskIndex + 1], CardType.SIMPLE_TRANSFER, [], selectedCard.ID);
          return;
        }
      }

      const cardIds = Object.keys(cardStateCards);
      // eslint-disable-next-line radix
      const nextCardIndex = cardIds.findIndex((e) => parseInt(e) === selectedCard.ID) + 1;

      const nextCard = cardStateCards[cardIds[nextCardIndex]];
      if (nextCard) {
        taskListBusinessSelectCard(nextCard.ID);
        cardStateActionSetCardIsExpanded(nextCard.ID, true);
        if (nextCard.type === CardType.SERIAL_DILUTION) {
          const firstSdStep = getFirstSdStep(nextCard);
          const taskList = getSdRawTaskList(firstSdStep, nextCard);
          onSelectTask(taskList[0].ID, nextCard.type, taskList, nextCard.ID, true, {
            taskNumber: firstSdStep,
            bypassValidityChecking: false,
          });
          return;
        }
        if (nextCard.type === CardType.NORMALIZATION) {
          const taskList = getNormalizationTasks(1, nextCard);
          onSelectTask(1, nextCard.type, taskList, nextCard.ID);
          return;
        }
        if (nextCard.type === CardType.BROADCAST) {
          const taskList = getBroadcastTasks(1, nextCard);
          onSelectTask(1, nextCard.type, taskList, nextCard.ID);
          return;
        }
        if (nextCard.type === CardType.POOLING) {
          const taskList = getPoolingTasks(1, nextCard);
          onSelectTask(1, nextCard.type, taskList, nextCard.ID);
          return;
        }
        const taskList = getTaskFromCardState(TaskType.PICK_TIP, nextCard);
        onSelectTask(nextCard.tasks[0], nextCard.type, taskList, nextCard.ID);
      }
    }
  };

  const onArrowUpPress = () => {
    if (highlightedTask) {
      if (highlightedTask.type === CardType.SERIAL_DILUTION && highlightedTask.ID !== getFirstSdStep(selectedCard)) {
        const prevStep = getPrevSdStep(selectedCard, highlightedTask.ID);
        const taskList = getSdRawTaskList(prevStep, selectedCard);
        onSelectTask(prevStep, highlightedTask.type, taskList, selectedCard.ID, true, {
          taskNumber: prevStep,
          bypassValidityChecking: false,
        });
        return;
      }
      if (highlightedTask.type === CardType.NORMALIZATION && highlightedTask.ID !== 1) {
        const taskList = getNormalizationTasks(highlightedTask.ID - 1, selectedCard);
        onSelectTask(highlightedTask.ID - 1, highlightedTask.type, taskList, selectedCard.ID);
        return;
      }
      if (highlightedTask.type === CardType.BROADCAST && highlightedTask.ID !== 1) {
        const taskList = getBroadcastTasks(highlightedTask.ID - 1, selectedCard);
        onSelectTask(highlightedTask.ID - 1, highlightedTask.type, taskList, selectedCard.ID);
        return;
      }
      if (highlightedTask.type === CardType.POOLING && highlightedTask.ID !== 1) {
        const taskList = getPoolingTasks(highlightedTask.ID - 1, selectedCard);
        onSelectTask(highlightedTask.ID - 1, highlightedTask.type, taskList, selectedCard.ID);
        return;
      }

      if (
        highlightedTask.type !== CardType.NORMALIZATION &&
        highlightedTask.type !== CardType.SERIAL_DILUTION &&
        highlightedTask.type !== CardType.BROADCAST &&
        highlightedTask.type !== CardType.POOLING
      ) {
        const taskIds = selectedCard.tasks;
        const currentSelectedTaskIndex = taskIds.findIndex((e) => e === highlightedTask.ID);

        if (currentSelectedTaskIndex !== 0) {
          onSelectTask(taskIds[currentSelectedTaskIndex - 1], highlightedTask.type, [], selectedCard.ID);
          return;
        }
      }

      const cardIds = Object.keys(cardStateCards);
      // eslint-disable-next-line radix
      const prevCardIndex = cardIds.findIndex((e) => parseInt(e) === selectedCard.ID) - 1;
      const prevCard = cardStateCards[cardIds[prevCardIndex]];
      if (prevCard) {
        taskListBusinessSelectCard(prevCard.ID);
        cardStateActionSetCardIsExpanded(prevCard.ID, true);
        if (prevCard.type === CardType.SERIAL_DILUTION) {
          const taskList = getSdRawTaskList(SerialDilutionStep.SET_DILUENT_TARGET, prevCard);
          onSelectTask(SerialDilutionStep.SET_DILUENT_TARGET, prevCard.type, taskList, prevCard.ID);
          return;
        }
        if (prevCard.type === CardType.NORMALIZATION) {
          const taskList = getNormalizationTasks(3, prevCard);
          onSelectTask(3, prevCard.type, taskList, prevCard.ID);
          return;
        }
        if (prevCard.type === CardType.BROADCAST) {
          const taskList = getBroadcastTasks(3, prevCard);
          onSelectTask(3, prevCard.type, taskList, prevCard.ID);
          return;
        }
        if (prevCard.type === CardType.POOLING) {
          const taskList = getPoolingTasks(3, prevCard);
          onSelectTask(3, prevCard.type, taskList, prevCard.ID);
          return;
        }
        onSelectTask(prevCard.tasks[prevCard.tasks.length - 1], prevCard.type, [], prevCard.ID);
      }
    }
  };

  // #endregion highlight task

  const isSerialDilutionCardIsIgnored = (card) => {
    if (card.type !== CardType.SERIAL_DILUTION) {
      return false;
    }

    const activeTasks = filterToArray(card?.sdRawTasks).filter((e) => !e.isIgnored);

    return activeTasks.length === 0;
  };

  const renameCardNameHandler = (cardID, cardName, value) => {
    if (cardName === value) {
      return;
    }
    cardActionSetCardName(cardID, value);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  // #region card group
  const closeGroup = () => {
    cardActionSetGroupIsExpanded();
    cardStateActionSetCardIsExpanded([], false);
  };

  const selectGroup = (ID, unselectOther = true, status = true) => {
    cardStateActionSetGroupIsSelected(ID, unselectOther, status);
    const selectedCardInsideGroup = Object.values(cardStateCards).filter((c) => c.groupID === ID);
    const selectedIds = selectedCardInsideGroup.map((c) => c.ID);
    taskListBusinessSelectCard(selectedIds, unselectOther, status);
    cardStateActionSetCardIsExpanded([], false);
  };

  const handleCardGroupMouseDown = (evt, ID, isExpanded, groupIndex, isContexMenu = false) => {
    // prevent to select group if is in serial dilution or broadcast or pooling or normalization or mixing mode
    if (
      protocolEditorMode === ProtocolEditorViewMode.SERIAL_DILUTION ||
      protocolEditorMode === ProtocolEditorViewMode.NORMALIZATION ||
      protocolEditorMode === ProtocolEditorViewMode.BROADCAST ||
      protocolEditorMode === ProtocolEditorViewMode.POOLING ||
      protocolEditorMode === ProtocolEditorViewMode.MIXING
    ) {
      return;
    }

    dispatch(checkVolumeModalAndApplyIfOpened());

    if (selectedCard.isInsertTask) {
      const newInsertTask = getInsertTask();
      onDeleteTask(newInsertTask.ID, selectedCard.ID, false, false);
    }

    if (!isContexMenu) {
      cardContexMenuRef.current.hide();
    }
    const newSelectedGroup = cardStateGroups[ID];

    if (evt?.shiftKey) {
      if (isExpanded) {
        closeGroup(ID);
      }

      onShiftSelect(groupIndex);
      protocolEditorStateActionSetSelectedTipChannel(null);
      protocolEditorStateActionSetCustomTipCount(null);

      if (_.isEmpty(startingGroup.current)) {
        setStartingGroup(groupIndex);
      }
      return;
    }

    if (evt?.ctrlKey) {
      batch(async () => {
        const status = !newSelectedGroup.isSelected;
        if (isExpanded) {
          closeGroup(ID);
        }
        selectGroup(ID, false, status);
        protocolEditorStateActionSetSelectedTipChannel(null);
        protocolEditorStateActionSetCustomTipCount(null);
        setStartingGroup(groupIndex);
      });
      return;
    }

    const selectedCards = Object.values(cardStateCards).filter((c) => c.groupID !== ID && c.isSelected);
    const selectedCardIds = selectedCards.map((c) => c.ID);
    taskListBusinessSelectCard(selectedCardIds, false, false);
    if (
      !selectedGroup.current.includes(ID) ||
      (selectedGroup.current.includes(ID) && selectedGroup.current.length > 1)
    ) {
      selectGroup(ID);
      setStartingGroup(groupIndex);
    }
    protocolEditorStateActionSetSelectedTipChannel(null);
    protocolEditorStateActionSetCustomTipCount(null);
  };

  const handleCardGroupExpand = (ID, isExpanded) => {
    if (runtimeMode && runtimeStateStatus === RuntimeMode.RUNNING) return;

    dispatch(checkVolumeModalAndApplyIfOpened());
    cardActionSetGroupIsExpanded(ID, !isExpanded);

    const expandedGroup = cardStateGroups[ID];
    expandedGroup.isExpanded = !isExpanded;
    const groups = {...cardStateGroups, [ID]: expandedGroup};
    undoRedoBusinessActionGlobalUndoRedoUpdatePresentState('cardState', {
      cards: cardStateCards,
      tasks: cardStateTasks,
      groups,
    });
  };

  const renameCardGroupNameHandler = (groupID, cardName, value) => {
    if (cardName === value) {
      return;
    }
    cardActionSetCardGroupName(groupID, value);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const onCheckGroup = (cards, checked) => {
    cardContexMenuRef.current.hide();
    const otherCardIDs = [];

    Object.values(cards).forEach((card) => {
      if (card.type === CardType.SERIAL_DILUTION) {
        taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks(card.ID, !checked);
      } else {
        otherCardIDs.push(card.ID);
      }
    });

    taskListBusinessSetIgnoreAllCards(otherCardIDs, !checked);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const getIgnoredCards = (cards) => {
    const notIgnoredCard = Object.values(cards).find((card) => !card.isIgnored);
    return !!notIgnoredCard;
  };

  const onDragCardGroup = (itemID) => {
    const [, , , draggedIDstr] = itemID.split('-');
    const draggedID = parseInt(draggedIDstr, 10);

    const selectedGroups = getSelectedGroups();
    const selectedGroupIds = selectedGroups.map((c) => c.ID);

    if (!selectedGroupIds.includes(draggedID)) {
      handleCardGroupMouseDown(null, draggedID);
    }
  };

  const onDropCardGroup = (item, itemID) => {
    const [, , draggedType] = item.ID.split('-');
    const [, , , droppedIDstr] = itemID.split('-');
    const droppedID = parseInt(droppedIDstr, 10);

    if (draggedType === 'group') return;

    const selectedCards = getSelectedCards();
    const selectedCardIds = selectedCards.map((c) => c.ID);
    const selectedCardsInGroup = selectedCards.filter((c) => c.groupID === droppedID);
    const selectedCardsInGroupIds = selectedCardsInGroup.map((c) => c.ID);

    const {cards} = cardStateGroups[droppedID];

    const lastCardID = cards[cards.length - 1];
    let start = lastCardID;

    if (selectedCardsInGroupIds.includes(lastCardID)) {
      start = getPreviousID(start, selectedCardsInGroupIds);
    }

    taskListBusinessAddCardGroup(selectedCardIds, start, droppedID, false);
  };

  const onCardGrouping = () => {
    const selectedCards = getSelectedCards();

    const cardInGroup = selectedCards.find((c) => c.groupID !== null && c.groupID !== undefined);

    if (cardInGroup) return;

    const selectedCardIds = selectedCards.map((c) => c.ID);
    const selectedGroups = getSelectedGroups();
    if (selectedCardIds.length && selectedGroups.length === 0) {
      taskListBusinessAddCardGroup(selectedCardIds, selectedCardIds[0]);
    }
  };

  const onCardUngrouping = () => {
    if (selectedGroup.current.length) {
      taskListBusinessUngroupCardGroup(selectedGroup.current);
    }
  };

  const onCardGroupDelete = () => {
    if (protocolEditorMode !== ProtocolEditorViewMode.SIMPLE_TRANSFER) {
      return;
    }

    const selectedCards = getSelectedCards();
    const selectedCardIds = selectedCards.map((c) => c.ID);
    const selectedGroups = getSelectedGroups();
    const selectedGroupIds = selectedGroups.map((g) => g.ID);

    selectedGroupIds.forEach((groupID) => {
      const {cards} = cardStateGroupsRef.current[groupID];
      selectedCardIds.push(...cards);
    });

    if (!selectedCardIds.length) {
      return;
    }

    const deleteProcess = () => {
      taskListBusinessDeleteMultipleCards(_.uniq(selectedCardIds), true);
      tasklistPicklistBusinessRemoveUnusedBarcode();
      setStartingGroup();

      appStateActionShowSnackbar(
        Feedback.SUCCESS,
        intl.formatMessage({
          id: 'protocolEditor.taskList.removeCard.notification.success',
        }),
      );
    };

    if (deleteCardValidation(selectedCardIds)) {
      showDeleteConfirmationModal(deleteProcess);
    } else {
      deleteProcess();
    }
  };

  // #endregion

  // #region check/uncheck cards

  const onCardCheckUncheck = (isChecked) => {
    const isValidCard = (c) => !c.invalidTypes || c.invalidTypes?.length === 0;
    const selectedCards = getSelectedCards();
    const selectedCardIds = [];

    Object.values(selectedCards).forEach((card) => {
      if (isValidCard(card)) {
        if (card.type === CardType.SERIAL_DILUTION) {
          taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks(card.ID, !isChecked);
        } else {
          selectedCardIds.push(card.ID);
        }
      }
    });

    const selectedGroups = getSelectedGroups();
    selectedGroups.forEach((group) => {
      const {cards} = cardStateGroupsRef.current[group.ID];
      cards.forEach((cardId) => {
        const cardInGroup = cardStateCardsRef.current[cardId];
        if (isValidCard(cardInGroup)) {
          if (cardInGroup.type === CardType.SERIAL_DILUTION) {
            taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks(cardInGroup.ID, !isChecked);
          } else {
            selectedCardIds.push(cardInGroup.ID);
          }
        }
      });
    });

    taskListBusinessSetIgnoreAllCards(selectedCardIds, !isChecked);
    undoRedoBusinessActionGlobalAddUndoItem();
  };

  const isDisableCheckUncheck = (isChecked) => {
    const isValidCard = (c) => !c?.invalidTypes || c?.invalidTypes?.length === 0;

    const selectedCards = getSelectedCards();
    const selectedGroups = getSelectedGroups();
    selectedGroups.forEach((group) => {
      const {cards} = cardStateGroupsRef.current[group.ID];
      cards.forEach((cardId) => {
        if (cardStateCardsRef.current[cardId]) {
          selectedCards.push(cardStateCardsRef.current[cardId]);
        }
      });
    });

    const checkedCard = selectedCards.find((card) => card?.isIgnored === false || card?.isIgnored === undefined);
    const uncheckedCard = selectedCards.find((card) => card?.isIgnored === true && isValidCard(card));
    const invalidCard = selectedCards.find((card) => card?.invalidTypes?.length);
    const validCard = selectedCards.find((card) => isValidCard(card));

    const isDisableCheck = !isChecked && checkedCard && !uncheckedCard;
    const isDisableUncheck = isChecked && !checkedCard && uncheckedCard;
    const isDisableBoth = invalidCard && (selectedCards.length === 1 || (selectedCards.length > 1 && !validCard));
    const isConsolidateTipsExist = isConsolidateTipsCardExist(selectedCards);

    return isDisableCheck || isDisableUncheck || isDisableBoth || isConsolidateTipsExist;
  };

  // #endregion check/uncheck cards

  // #region context menu
  const onShowCardMenuContext = (e, ID, cardType, groupIndex, cardIndex) => {
    if (runtimeMode) return;
    setContextMenuTask({anchorEl: null, contextMenu: null});

    const selectedCards = getSelectedCards();
    const isSelected = selectedCards.find((c) => c.ID === ID);
    if (!isSelected) {
      handleMouseDownCard(e, ID, cardType, groupIndex, cardIndex, true);
    }
    cardContexMenuRef.current.show(e);
  };

  const onShowCardGroupMenuContext = (e, ID, isExpanded, index) => {
    if (runtimeMode) return;
    setContextMenuTask({anchorEl: null, contextMenu: null});

    const selectedGroups = getSelectedGroups();
    const isSelected = selectedGroups.find((g) => g.ID === ID);
    if (!isSelected) {
      handleCardGroupMouseDown(e, ID, isExpanded, index, true);
    }
    cardContexMenuRef.current.show(e);
  };

  const isDisableDuplicate = () => isConsolidateTipsCardExist(getSelectedCards()) || isReadOnly;

  // check is access to group/ungroup feature disabled
  const isDisableGrouping = () => {
    const selectedCards = getSelectedCards();
    const isConsolidateTipsExist = isConsolidateTipsCardExist(selectedCards);

    return isConsolidateTipsExist || isReadOnly;
  };

  // check is the action of grouping disabled
  const isDisableGroup = () => {
    const selectedCards = getSelectedCards();
    const isCardGroup = selectedCards.find((card) => card.groupID !== null && card.groupID !== undefined);

    return !!isCardGroup || isDisableGrouping();
  };

  const onShowTaskContextMenu = (e, id, cardType, taskList, cardID) => {
    if (runtimeMode) return;
    cardContexMenuRef.current.hide();

    if (highlightedTask?.ID !== id) {
      onSelectTask(id, cardType, taskList, cardID, false);
    }

    const taskType = cardStateTasks[id]?.type;
    const isAllowed = ALLOWED_OPEN_CONTEXT_MENU_TASK_TYPES.includes(taskType);

    if (!isAllowed) {
      if (contextMenuTask.contextMenu) setContextMenuTask({anchorEl: null, contextMenu: null});
      return;
    }

    setContextMenuTask({
      contextMenu:
        contextMenuTask.contextMenu === null
          ? {
              mouseX: e.clientX + 2,
              mouseY: e.clientY - 6,
            }
          : null,
      anchorEl: contextMenuTask.contextMenu === null ? e.currentTarget : null,
    });
  };

  const hideTaskContextMenu = () => {
    setContextMenuTask({anchorEl: null, contextMenu: null});
    onSelectTask();
  };
  // #endregion context menu

  // #region shortcut

  const allowedKeyCode = [KeyboardKey.D, KeyboardKey.G, KeyboardKey.DELETE];

  const onKeyPress = (evt) => {
    const {keyCode} = evt;

    const isShiftKeyPressed = evt.shiftKey;
    const isCtrlKeyPressed = evt.ctrlKey;
    const isDeleteKeyPressed = keyCode === KeyboardKey.DELETE;

    if (runtimeMode || isModalShowing || isLoadingShowing || userManagemenBusinessIsUserOperator()) {
      return;
    }

    // if not pressing modifier key
    if (!isShiftKeyPressed && !isCtrlKeyPressed && !isDeleteKeyPressed) {
      keyCodePressedList.current = [];
      return;
    }

    // if pressing unregistered keys
    if (!allowedKeyCode.includes(keyCode)) {
      return;
    }

    const keyCodeList = keyCodePressedList.current;

    if (!keyCodeList.includes(keyCode)) {
      keyCodeList.push(keyCode);
    }

    if (isCtrlKeyPressed) {
      // Duplicate card
      const isDKeyPressed = keyCodeList.includes(KeyboardKey.D);
      if (isDKeyPressed && !isDisableDuplicate()) {
        onKeyUp(evt);
        onDuplicateCard();
        return;
      }

      // Group && ungroup card
      const isGKeyPressed = keyCodeList.includes(KeyboardKey.G);
      if (isShiftKeyPressed && isGKeyPressed && !isDisableGrouping()) {
        onKeyUp(evt);
        onCardUngrouping();
        return;
      }

      if (isGKeyPressed && !isDisableGrouping()) {
        onKeyUp(evt);
        onCardGrouping();
        return;
      }
    }

    if (isDeleteKeyPressed && evt?.target?.localName !== 'input') {
      onKeyUp(evt);
      onCardGroupDelete();
    }

    if (keyCodeList.length >= MAX_KEYCODE_COMBINATION) {
      onKeyUp(evt, false);
    }
  };

  const onKeyUp = (evt, isPreventDefault = true) => {
    if (isPreventDefault) evt.preventDefault();

    const {keyCode} = evt;
    if (!allowedKeyCode.includes(keyCode)) {
      return;
    }

    keyCodePressedList.current = keyCodePressedList.current.filter((key) => key !== keyCode);
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyPress);
    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeMode, isModalShowing, isLoadingShowing, protocolEditorMode]);

  useEffect(() => {
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCardsRef = (cardRefParam, ID) => {
    eachCardsRef.current[ID] = {current: cardRefParam};
  };

  const resettingSelectedTaskExcludedComponentRefs = [
    svmExcludedComponentRef,
    menuListRef,
    ...(Object.values(deckLabwareRefs || {}) || []),
  ];

  useClickOutside(
    cardRef,
    () => (contextMenuTask.contextMenu ? null : onSelectTask()),
    resettingSelectedTaskExcludedComponentRefs,
    {useCapture: true},
  );

  // #endregion shortcut

  const renderDelayTask = (card) =>
    Object.values(_.pick(cardStateTasks, card.tasks)).map((item) => (
      <TaskDelay
        key={item.ID}
        ID={card.ID}
        initialTask={item}
        onApply={(task) => dispatch(checkAndApplyGeneralTask(task, card))}
        runtimeMode={runtimeMode}
        executionStatus={getTaskExecutionStatus(item.ID)}
        isCardMode
        isIgnored={card.isIgnored}
        onCheckedChange={() => {}}
        onDelete={() => {}}
        drag={() => {}}
        isDragging={false}
        invalid={false}
        onSelectTask={(ID) => onSelectTask(ID, card.type, [item], card.ID)}
        isHighlightedTask={highlightedTask?.ID === item.ID}
        deleteModalDelayShowing={deleteModalShowing}
        excludedComponentRef={[refList, cardRef]}
        onArrowDownPress={onArrowDownPress}
        onArrowUpPress={onArrowUpPress}
        isViewMode={isReadOnly}
      />
    ));

  const renderConfirmationTask = (card) =>
    Object.values(_.pick(cardStateTasks, card.tasks)).map((item) => (
      <TaskConfirmation
        key={item.ID}
        ID={card.ID}
        initialTask={item}
        onApply={(task) => dispatch(checkAndApplyGeneralTask(task, card))}
        runtimeMode={runtimeMode}
        executionStatus={getTaskExecutionStatus(item.ID)}
        isCardMode
        isIgnored={card.isIgnored}
        onCheckedChange={() => {}}
        onDelete={() => {}}
        drag={() => {}}
        isDragging={false}
        invalid={false}
        onSelectTask={(ID) => onSelectTask(ID, card.type, [item], card.ID)}
        isHighlightedTask={highlightedTask?.ID === item.ID}
        deleteModalConfirmationShowing={deleteModalShowing}
        excludedComponentRef={[refList, cardRef]}
        onArrowDownPress={onArrowDownPress}
        onArrowUpPress={onArrowUpPress}
        isViewMode={isReadOnly}
      />
    ));

  const renderBroadcastTasks = (card) => {
    const tasks = Object.values(_.pick(cardStateTasks, card.tasks)).sort(
      (a, b) => card.tasks.indexOf(a) - card.tasks.indexOf(b),
    );
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (tasks?.length <= 0) return <></>;

    const isEdit = !!protocolBroadcastTaskEditStep;

    const editBroadcastTaskHandler = async (number) => {
      await onTaskEdit(number, card.ID, card.type);
      undoRedoBusinessActionGlobalAddUndoItem();
    };

    const deleteBroadcastTaskHandler = (number) => {
      taskListBroadcastBusinessBackHandler();
      notifyDeleteTask(number, card.ID, card.type);
    };

    const broadcastReuseTipsHandler = (checked) => {
      const reuseTipsHandler = (isChecked) => {
        taskListBroadcastBusinessOnBroadcastReuseTipChange(isChecked);
        dispatch(restoreTipCountForSingleCard(card.ID));
        undoRedoBusinessActionGlobalAddUndoItem();
      };

      if (!card?.reuseTips) {
        modalStateBusinessAction(() => {
          reuseTipsHandler(checked);
        });
      } else {
        reuseTipsHandler(checked);
      }
    };

    const aspirateTasks = tasks.filter((item) => item.type === TaskType.ASPIRATE);
    const taskAspirateLabware = find(labwareState, {LabwareID: aspirateTasks[0]?.labwareID});

    const dispenseTasks = tasks.filter((item) => item.type === TaskType.DISPENSE);
    const taskDispenseLabware = find(labwareState, {LabwareID: dispenseTasks[0]?.labwareID});
    const elementTasks = [];

    if (aspirateTasks?.length > 0 && taskAspirateLabware) {
      elementTasks.push(
        <BroadcastTasksContainer
          cardID={card?.ID}
          disallowEdit={(taskId) =>
            disallowEditBroadcast(taskId, runtimeMode, protocolBroadcastTaskEditStep, protocolEditorMode)
          }
          tasks={aspirateTasks}
          labware={taskAspirateLabware}
          labwareState={labwareState}
          isHighlighted={highlightedTask}
          onArrowDownPress={onArrowDownPress}
          onArrowUpPress={onArrowUpPress}
          onSelectTask={onSelectTask}
          onEdit={editBroadcastTaskHandler}
          onDelete={deleteBroadcastTaskHandler}
          readOnly={isReadOnly || onGetCardExecutionStatus(card?.ID) === ExecutionStatus.EXECUTED}
          onReuseTipsChange={broadcastReuseTipsHandler}
          reuseTips={card?.reuseTips}
          showEditButton={!isReadOnly && !isEdit}
          executionStatus={(taskId) =>
            getBroadcastTaskExecutionStatus(
              taskId,
              card,
              runtimeMode,
              runtimeStateStatus,
              cardStateTasks,
              runtimeStateCsvRecordID,
            )
          }
          runtimeMode={runtimeMode}
        />,
      );
    }
    if (dispenseTasks?.length > 0 && taskDispenseLabware) {
      elementTasks.push(
        <BroadcastTasksContainer
          cardID={card?.ID}
          disallowEdit={(taskId) =>
            disallowEditBroadcast(taskId, runtimeMode, protocolBroadcastTaskEditStep, protocolEditorMode)
          }
          tasks={dispenseTasks}
          labware={taskDispenseLabware}
          labwareState={labwareState}
          isHighlighted={highlightedTask}
          onArrowDownPress={onArrowDownPress}
          onArrowUpPress={onArrowUpPress}
          onSelectTask={onSelectTask}
          onEdit={editBroadcastTaskHandler}
          onDelete={deleteBroadcastTaskHandler}
          readOnly={isReadOnly || onGetCardExecutionStatus(card?.ID) === ExecutionStatus.EXECUTED}
          reuseTips={card?.reuseTips}
          onReuseTipsChange={broadcastReuseTipsHandler}
          showEditButton={!isReadOnly && !isEdit}
          executionStatus={(taskId) =>
            getBroadcastTaskExecutionStatus(
              taskId,
              card,
              runtimeMode,
              runtimeStateStatus,
              cardStateTasks,
              runtimeStateCsvRecordID,
            )
          }
          runtimeMode={runtimeMode}
        />,
      );
    }

    return elementTasks;
  };

  const renderPoolingTasks = (card) => {
    const tasks = Object.values(_.pick(cardStateTasks, card.tasks)).sort(
      (a, b) => card.tasks.indexOf(a) - card.tasks.indexOf(b),
    );
    const taskElements = [];

    const taskAspirates = tasks.filter((e) => e.type === TaskType.ASPIRATE);
    const taskAspirateLabware = find(labwareState, {LabwareID: taskAspirates[0]?.labwareID});
    const taskAspirateVolume = taskAspirates[0]?.volume;

    const taskDispenses = tasks.filter((e) => e.type === TaskType.DISPENSE);
    const taskDispenseLabware = find(labwareState, {LabwareID: taskDispenses[0]?.labwareID});

    const hasAnyAspirateTasks = taskAspirates.length > 0;
    const hasAnyDispenseTasks = taskDispenses.length > 0;

    const isEdit = !!protocolPoolingTaskEditStep;

    const editPoolingTaskHandler = async (number) => {
      await onTaskEdit(number, card.ID, card.type);
      undoRedoBusinessActionGlobalAddUndoItem();
    };

    const deletePoolingTaskHandler = (number) => {
      taskListPoolingBusinessBackHandler(true);
      notifyDeleteTask(number, card.ID, card.type);
    };

    const poolingReuseTipsHandler = (checked) => {
      const reuseTipsHandler = (isChecked) => {
        taskListPoolingBusinessOnPoolingReuseTipChange(isChecked);
        undoRedoBusinessActionGlobalAddUndoItem();
      };
      if (!card?.reuseTips) {
        modalStateBusinessAction(() => {
          reuseTipsHandler(checked);
        });
      } else {
        reuseTipsHandler(checked);
      }
    };

    // const countLabwareDefinitionID = () => selectDistinct(taskAspirates.map((task) => {
    //   const inUsedLabware = find(labwareState, { LabwareID: task.labwareID });
    //   return inUsedLabware?.IDPrefix;
    // })).length;

    if (hasAnyAspirateTasks && taskAspirateLabware) {
      taskElements.push(
        <PoolingTasksContainer
          card={card}
          cardStateTasks={cardStateTasks}
          disallowEdit={(taskId) =>
            disallowEditPooling(taskId, runtimeMode, protocolPoolingTaskEditStep, protocolEditorMode)
          }
          executionStatus={(taskId) =>
            getPoolingTaskExecutionStatus(
              taskId,
              card,
              runtimeMode,
              runtimeStateStatus,
              cardStateTasks,
              runtimeStateCsvRecordID,
            )
          }
          isHighlighted={highlightedTask}
          tasks={taskAspirates}
          labware={taskAspirateLabware}
          labwareState={labwareState}
          showEditButton={!userManagemenBusinessIsUserOperator() && !isEdit}
          showDeleteButton={!isEdit && !hasAnyDispenseTasks && !taskAspirateVolume}
          onEdit={editPoolingTaskHandler}
          onDelete={deletePoolingTaskHandler}
          runtimeMode={runtimeMode}
          runtimeStateStatus={runtimeStateStatus}
          readOnly={
            isReadOnly ||
            onGetCardExecutionStatus(card.ID) === ExecutionStatus.EXECUTED ||
            userManagemenBusinessIsUserOperator()
          }
          onSelectTask={onSelectTask}
          reuseTips={card?.reuseTips}
          onReuseTipsChange={poolingReuseTipsHandler}
        />,
      );
    }

    if (hasAnyDispenseTasks && taskDispenseLabware) {
      taskElements.push(
        <PoolingTasksContainer
          card={card}
          cardStateTasks={cardStateTasks}
          disallowEdit={(taskId) =>
            disallowEditPooling(taskId, runtimeMode, protocolPoolingTaskEditStep, protocolEditorMode)
          }
          executionStatus={(taskId) =>
            getPoolingTaskExecutionStatus(
              taskId,
              card,
              runtimeMode,
              runtimeStateStatus,
              cardStateTasks,
              runtimeStateCsvRecordID,
            )
          }
          isHighlighted={highlightedTask}
          tasks={taskDispenses}
          labware={taskDispenseLabware}
          labwareState={labwareState}
          showEditButton={!userManagemenBusinessIsUserOperator() && !isEdit}
          showDeleteButton={!isEdit && !hasAnyDispenseTasks && !taskAspirateVolume}
          onEdit={editPoolingTaskHandler}
          onDelete={deletePoolingTaskHandler}
          runtimeMode={runtimeMode}
          runtimeStateStatus={runtimeStateStatus}
          readOnly={
            isReadOnly ||
            onGetCardExecutionStatus(card.ID) === ExecutionStatus.EXECUTED ||
            userManagemenBusinessIsUserOperator()
          }
          onSelectTask={onSelectTask}
        />,
      );
    }

    return taskElements;
  };

  const renderConsolidateTipsTask = (card) => {
    const tasks = Object.values(_.orderBy(_.pick(cardStateTasks, card.tasks), ['number'], ['asc']));
    const task = tasks?.[0];

    return (
      <TaskConsolidateTips
        number={1}
        isHighlighted={
          highlightedTask?.type === CardType.CONSOLIDATE_TIPS &&
          highlightedTask?.cardID === card.ID &&
          highlightedTask?.ID === 1
        }
        runtimeMode={runtimeMode}
        executionStatus={getConsolidateTipsExecutionStatus()}
        onSelectTask={() => onSelectTask(1, card.type, [task], card.ID)}
      />
    );
  };

  const renderCards = (card, idx, groupIndex) =>
    isCardVisible(card) ? (
      <>
        {(idx === 0 || (groupIndex === 0 && idx === '#')) && (
          <CardDropZone
            itemID={`${groupIndex}-${idx}-card-${card.ID}-first`}
            acceptedItems={
              card.groupID !== null && card.groupID !== undefined
                ? [DragDropType.TASK_CARD]
                : [DragDropType.TASK_CARD, DragDropType.CARD_GROUP]
            }
            isDisableDrop={(item) => isDisableCardDropZone(item, groupIndex, idx, true)}
            onDrop={(item, itemID) => onRearrangeCardDrop(item, itemID, card?.groupID)}
          />
        )}
        <DraggableAndDroppableCard
          ref={(e) => {
            if (card.isSelected) {
              cardRef.current = e;
            }
            if (card.isSelected && svmExcludedComponentRef?.current) {
              svmExcludedComponentRef.current = e;
            }

            if (card.isExpanded) {
              expandedCardRef.current = e;
            }

            addToCardsRef(e, card.ID);
          }}
          itemID={`${groupIndex}-${idx}-card-${card.ID}`}
          itemType={DragDropType.TASK_CARD}
          onDrop={onCardDrop}
          acceptedItems={[DragDropType.TASK_CARD]}
          onDrag={onDragCard}
          disableDrag={runtimeMode || userManagemenBusinessIsUserOperator() || isReadOnly}
          isDisableDrop={(item) => isDisableCardDrop(item, card)}
          disablePreview
          key={`${card.ID}-${card.tasks[0]}`}
          ID={card.ID}
          name={card.name}
          isSelected={card.isSelected}
          onMouseDown={(e, id, type) => handleMouseDownCard(e, id, type, groupIndex, idx)}
          onMouseUp={handleMouseUpCard}
          onCheckedAllChange={onCheckedCardChange}
          onDelete={(ID, cardType) => onDeleteCard(ID, cardType, card.subType === SubCardType.PICKLIST)}
          runtimeMode={runtimeMode}
          executionStatus={onGetCardExecutionStatus(card.ID)}
          invalid={!!card.invalidTypes?.length && !card.invalidTypes?.includes(CardInvalidType.UNFINISHED)}
          invalidTypes={card.invalidTypes}
          isIgnored={card.isIgnored || isSerialDilutionCardIsIgnored(card)}
          isExpanded={card.isExpanded}
          onMouseDownExpand={(e, id) => handMouseDownExpandCard(e, id, groupIndex, idx)}
          onMouseUpExpand={handMouseUpExpandCard}
          showSettingIcon={false}
          settingIconDisabled={protocolEditorMode !== ProtocolEditorViewMode.SIMPLE_TRANSFER || !card.isSelected}
          onCardNameChange={renameCardNameHandler}
          hasHighlightedTask={highlightedTask?.cardID === card.ID}
          cardType={card.type}
          className={
            (card.type === CardType.CONFIRMATION || card.type === CardType.DELAY || card.type === CardType.MIXING) &&
            card.isSelected
              ? 'background-blue'
              : ''
          }
          maxCharacters={protocolConfig.cardNameMaxLength}
          runtimeStatus={runtimeStateStatus}
          isInsertTask={card.isInsertTask}
          tasks={selectedTaskList[card.ID]}
          onShowMenuContext={(e) => onShowCardMenuContext(e, card.ID, card.type, groupIndex, idx)}
          disableMenuContext={isReadOnly}
          cardGroup={card?.groupID}
          sdRawTasksSummary={card?.sdRawTasksSummary}
          onProgressRecordCSVIDs={runtimeStateOnProgressRecordCSVIDs}
          protocolPoolingTaskEditStep={protocolPoolingTaskEditStep}
          isViewMode={isReadOnly}
          hideCheckbox={card.type === CardType.CONSOLIDATE_TIPS}
          isNameReadOnly={card.type === CardType.CONSOLIDATE_TIPS}
          requiredTips={card.requiredTips}
        >
          {card.type === CardType.SIMPLE_TRANSFER && card.isExpanded && (
            <SimpleTransferTasksContainer
              card={card}
              onSelectTask={onSelectTaskWithObjectProps}
              onShowMenuContext={onShowTaskContextMenu}
              onDelete={(taskId, isShowConfirmationModal) =>
                onDeleteTask(taskId, card.ID, card.subType === SubCardType.PICKLIST, isShowConfirmationModal)
              }
              runtimeMode={runtimeMode}
              readOnly={isReadOnly}
              onArrowDownPress={onArrowDownPress}
              onArrowUpPress={onArrowUpPress}
              menuListRef={menuListRef}
              editablePickTipExcludedRef={editablePickTipExcludedRef}
              excludedComponentRef={[refList, cardRef]}
            />
          )}
          {card.type === CardType.NORMALIZATION && card.isExpanded && (
            <NormalizationTaskContainer
              card={card}
              editNormalizationTaskHandler={async (number) => {
                await onTaskEdit(number, card.ID, card.type);
                undoRedoBusinessActionGlobalAddUndoItem();
              }}
              onGetCardExecutionStatus={onGetCardExecutionStatus}
              runtimeMode={runtimeMode}
              userManagemenBusinessIsUserOperator={userManagemenBusinessIsUserOperator}
              onSelectTask={onSelectTask}
              highlightedTask={highlightedTask}
              onArrowDownPress={onArrowDownPress}
              onArrowUpPress={onArrowUpPress}
              readOnly={isReadOnly}
            />
          )}
          {card.type === CardType.SERIAL_DILUTION && card.isExpanded && (
            <SerialDilutionTasksContainer
              ref={addToCardsRef}
              card={card}
              onSelectTask={onSelectTaskWithObjectProps}
              runtimeMode={runtimeMode}
              readOnly={isReadOnly}
              onArrowDownPress={onArrowDownPress}
              onArrowUpPress={onArrowUpPress}
              highlightedTask={
                highlightedTask?.type === CardType.SERIAL_DILUTION && highlightedTask?.cardID === card.ID
                  ? highlightedTask
                  : null
              }
            />
          )}
          {card.type === CardType.DELAY && card.isExpanded && renderDelayTask(card)}
          {card.type === CardType.CONFIRMATION && card.isExpanded && renderConfirmationTask(card)}
          {card.type === CardType.BROADCAST && card.isExpanded && renderBroadcastTasks(card)}
          {card.type === CardType.POOLING && card.isExpanded && renderPoolingTasks(card)}
          {card.type === CardType.MIXING && card.isExpanded && (
            <MixingTasksContainer
              card={card}
              onSelectTask={onSelectTaskWithObjectProps}
              runtimeMode={runtimeMode}
              readOnly={isReadOnly}
              onArrowDownPress={onArrowDownPress}
              onArrowUpPress={onArrowUpPress}
              runtimeStateStatus={runtimeStateStatus}
              getTaskExecutionStatus={getTaskExecutionStatus}
              onDelete={(taskID, showDeleteConfirmation = true) =>
                onDeleteTask(taskID, card.ID, false, showDeleteConfirmation)
              }
              excludedComponentRef={[refList, cardRef]}
            />
          )}
          {card.type === CardType.CONSOLIDATE_TIPS && card.isExpanded && renderConsolidateTipsTask(card)}
        </DraggableAndDroppableCard>
        <CardDropZone
          itemID={`${groupIndex}-${idx}-card-${card.ID}`}
          acceptedItems={
            card.groupID !== null && card.groupID !== undefined
              ? [DragDropType.TASK_CARD]
              : [DragDropType.TASK_CARD, DragDropType.CARD_GROUP]
          }
          isDisableDrop={(item) => isDisableCardDropZone(item, groupIndex, idx)}
          onDrop={(item, itemID) => onRearrangeCardDrop(item, itemID, card?.groupID)}
          isLast={groupIndex === groupCard.length - 1}
        />
      </>
    ) : (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <></>
    );

  useEffect(() => {
    if (!Array.isArray(selectedCard.tasks)) return;
    // eslint-disable-next-line max-len
    const isTipNotEjected =
      getTasksInCard(selectedCard.tasks).findIndex((task) => task.type === TaskType.EJECT_TIP) === -1;
    if (isTipNotEjected) {
      refreshTaskContext(selectedCard.tasks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TaskList isReadOnly={isReadOnly}>
      {groupCard.map((group, index) =>
        typeof group.cards === 'object' ? (
          <>
            {index === 0 && (
              <CardDropZone
                key={`group-${group.ID}-first`}
                itemID={`${index}-#-group-${group.ID}-first`}
                acceptedItems={[DragDropType.TASK_CARD, DragDropType.CARD_GROUP]}
                isDisableDrop={(item) => isDisableCardDropZone(item, index, '#', true)}
                onDrop={(item, itemID) => onRearrangeCardDrop(item, itemID, null)}
              />
            )}
            <DraggableAndDroppableCardGroup
              key={`$group-${group.ID}-${group.cards.length}`}
              itemID={`${index}-#-group-${group.ID}`}
              itemType={DragDropType.CARD_GROUP}
              disableDrag={runtimeMode || isReadOnly}
              disablePreview
              onDrag={onDragCardGroup}
              onDrop={onDropCardGroup}
              acceptedItems={[DragDropType.TASK_CARD]}
              selected={group?.isSelected}
              isExpanded={group.isExpanded}
              isIgnored={getIgnoredCards(group.cards)}
              onClickExpand={handleCardGroupExpand}
              ID={group.ID}
              name={group.name}
              onMouseDownHandler={(e, id, isExpanded) => handleCardGroupMouseDown(e, id, isExpanded, index)}
              onShowMenuContext={(e) => onShowCardGroupMenuContext(e, group.ID, group.isExpanded, index)}
              onCheckGroup={(checked) => onCheckGroup(group.cards, checked)}
              onCardGroupNameChange={renameCardGroupNameHandler}
              maxCharacters={protocolConfig.cardNameMaxLength}
              runtimeMode={runtimeMode}
              isViewMode={isReadOnly}
              disableMenuContext={userManagemenBusinessIsUserOperator()}
            >
              {group.isExpanded && group.cards.map((card, idx) => renderCards(card, idx, index))}
            </DraggableAndDroppableCardGroup>
            <CardDropZone
              key={`group-${group.ID}`}
              itemID={`${index}-#-group-${group.ID}`}
              acceptedItems={[DragDropType.TASK_CARD, DragDropType.CARD_GROUP]}
              isDisableDrop={(item) => isDisableCardDropZone(item, index, '#')}
              onDrop={(item, itemID) => onRearrangeCardDrop(item, itemID, null)}
              isLast={index === groupCard.length - 1}
            />
          </>
        ) : (
          renderCards(group.card, '#', index)
        ),
      )}
      <CardContextMenu
        ref={cardContexMenuRef}
        onDuplicate={onDuplicateCard}
        onGroup={onCardGrouping}
        onUngroup={onCardUngrouping}
        onDelete={onCardGroupDelete}
        onCheck={() => onCardCheckUncheck(true)}
        onUncheck={() => onCardCheckUncheck(false)}
        hideContextMenu={() => cardContexMenuRef.current.hide()}
        isDisableContexMenu={protocolEditorMode !== ProtocolEditorViewMode.SIMPLE_TRANSFER}
        isGroup={!!selectedGroup.current.length}
        isDisableGroup={isDisableGroup()}
        isDisableCheck={isDisableCheckUncheck(false)}
        isDisableUncheck={isDisableCheckUncheck(true)}
        isDisableDuplicate={isDisableDuplicate()}
        card={{
          cardID: selectedCard.ID,
          cardName: selectedCard.name,
          cardType: selectedCard.type,
          tasks: getTasksInCard(selectedCard.tasks),
        }}
        isMultipleCardSelected={getSelectedCards().length > 1}
      />
      <TaskContextMenu
        hideContextMenu={hideTaskContextMenu}
        task={highlightedTask}
        isDisableContexMenu={userManagemenBusinessIsUserOperator()}
        contextMenuTask={contextMenuTask}
      />
    </TaskList>
  );
}

TaskListContainer.propTypes = {
  intl: PropTypes.any.isRequired,
  selectedCard: PropTypes.object,
  selectedTaskList: PropTypes.object,
  cardStateTasks: PropTypes.object.isRequired,
  cardStateGroups: PropTypes.object.isRequired,
  labwareState: PropTypes.object.isRequired,
  runtimeMode: PropTypes.bool,
  readOnly: PropTypes.bool,
  runtimeStateCsvRecordID: PropTypes.number.isRequired,
  runtimeStateOnProgressRecordCSVIDs: PropTypes.array.isRequired,
  runtimeStateStatus: PropTypes.string.isRequired,
  taskListBusinessSetIgnoreAllCards: PropTypes.func.isRequired,
  taskListBusinessSelectCard: PropTypes.func.isRequired,
  taskListBusinessRemoveCardTask: PropTypes.func.isRequired,
  taskListBusinessDeleteCard: PropTypes.func.isRequired,
  taskListBusinessCheckInvalidTask: PropTypes.func.isRequired,
  taskListBusinessAddCardGroup: PropTypes.func.isRequired,
  taskListBusinessUngroupCardGroup: PropTypes.func.isRequired,
  taskListBusinessRearrangeCardFromGroupCard: PropTypes.func.isRequired,
  taskListBusinessDeleteMultipleCards: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessGetSdRawTaskIds: PropTypes.func.isRequired,
  taskListSerialDilutionBusinessSetIgnoredAllSdRawTasks: PropTypes.func.isRequired,
  taskListBroadcastBusinessBackHandler: PropTypes.func.isRequired,
  taskListBroadcastBusinessOnBroadcastReuseTipChange: PropTypes.func.isRequired,
  tasklistPicklistBusinessRemoveUnusedBarcode: PropTypes.func.isRequired,
  cardStateActionSetCardIsExpanded: PropTypes.func.isRequired,
  cardActionSetTaskIsSelected: PropTypes.func.isRequired,
  cardSerialDilutionActionSetSdRawTaskIsSelected: PropTypes.func.isRequired,
  cardActionSetCardName: PropTypes.func.isRequired,
  cardActionSetCardGroupName: PropTypes.func.isRequired,
  cardActionSetGroupIsExpanded: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  appStateActionShowSnackbar: PropTypes.func.isRequired,
  protocolBusinessSaveProtocol: PropTypes.func.isRequired,
  onCardClick: PropTypes.func,
  onTaskEdit: PropTypes.func,
  notifyDeleteCard: PropTypes.func,
  notifyDeleteTask: PropTypes.func,
  taskListPoolingBusinessBackHandler: PropTypes.func.isRequired,
  taskListPoolingBusinessOnPoolingReuseTipChange: PropTypes.func.isRequired,
  // taskListMixingBusinessSaveMixingStateBeforeEdit: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalAddUndoItem: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalUndoRedoUpdatePresentState: PropTypes.func.isRequired,
  protocolConfig: PropTypes.object.isRequired,
  protocolEditorStateADModalShown: PropTypes.any.isRequired,
  protocolEditorStateActionSetSelectedTipChannel: PropTypes.func.isRequired,
  protocolEditorStateActionSetCustomTipCount: PropTypes.func.isRequired,
  protocolEditorStateActionSetSerialDilutionEditStep: PropTypes.func.isRequired,
  protocolBroadcastTaskEditStep: PropTypes.any,
  protocolEditorStateActionSetBroadcastTaskEditStep: PropTypes.func.isRequired,
  protocolPoolingTaskEditStep: PropTypes.any,
  protocolEditorStateActionSetPoolingTaskEditStep: PropTypes.func.isRequired,
  protocolEditorStateActionSetIsEditMixing: PropTypes.func.isRequired,
  protocolEditorMode: PropTypes.string,
  setProtocolEditorMode: PropTypes.func.isRequired,
  highlightedTask: PropTypes.any,
  setHighlightedTask: PropTypes.func,
  refList: PropTypes.any,
  isModalShowing: PropTypes.bool.isRequired,
  isLoadingShowing: PropTypes.bool.isRequired,
  taskListBusinessDuplicateCardsAndGroups: PropTypes.func.isRequired,
  cardStateActionSetGroupIsSelected: PropTypes.func.isRequired,
  modalStateBusinessAction: PropTypes.func.isRequired,
  userManagemenBusinessIsUserOperator: PropTypes.func.isRequired,
  virtualTrayRef: PropTypes.any.isRequired,
  menuListRef: PropTypes.any.isRequired,
  protocolTrayType: PropTypes.string.isRequired,
  editablePickTipExcludedRef: PropTypes.any.isRequired,
  svmExcludedComponentRef: PropTypes.any.isRequired,
};

TaskListContainer.defaultProps = {
  onCardClick: () => {},
  onTaskEdit: () => {},
  notifyDeleteCard: () => {},
  notifyDeleteTask: () => {},
  runtimeMode: false,
  readOnly: false,
  selectedCard: {},
  selectedTaskList: {},
  highlightedTask: null,
  setHighlightedTask: () => {},
  refList: null,
  protocolBroadcastTaskEditStep: null,
  protocolPoolingTaskEditStep: null,
  protocolEditorMode: '',
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(TaskListContainer));
